import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to load the Facebook SDK script.
 *
 * @param {Object} options - The options for loading the script.
 * @param {string} options.nonce - The nonce attribute for the script tag.
 * @param {Function} [options.onScriptLoadSuccess] - Callback function executed upon successful script load.
 * @param {Function} [options.onScriptLoadError] - Callback function executed if the script fails to load.
 * @returns {boolean} Indicates whether the script has loaded successfully.
 */
export default function useLoadFBScript(options) {
	const { nonce, onScriptLoadSuccess, onScriptLoadError } = options;

	const [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] = useState(false);

	const onScriptLoadSuccessRef = useRef(onScriptLoadSuccess);
	onScriptLoadSuccessRef.current = onScriptLoadSuccess;

	const onScriptLoadErrorRef = useRef(onScriptLoadError);
	onScriptLoadErrorRef.current = onScriptLoadError;

	useEffect(() => {
		const script = document.createElement('script');
		script.async = true;
		script.defer = true;
		script.crossOrigin = "anonymous";
		script.src = "https://connect.facebook.net/en_US/sdk.js";
		script.onload = () => {
			setScriptLoadedSuccessfully(true);
			onScriptLoadSuccessRef.current?.();
		};

		script.onerror = () => {
			setScriptLoadedSuccessfully(false);
			onScriptLoadErrorRef.current?.();
		};

		document.body.appendChild(script);
		return () => {
			document.body.removeChild(script);
		};
	}, [nonce]);

	return scriptLoadedSuccessfully;
}

useLoadFBScript.propTypes = {
	options: PropTypes.shape({
		nonce: PropTypes.string,
		onScriptLoadSuccess: PropTypes.func,
		onScriptLoadError: PropTypes.func
	}).isRequired,
};
